import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { SscShellComponent } from '../ssc-shell/ssc-shell';

type AssetTab = 'glossary' | 'tm' | 'corpus' | 'benchmark';

interface Summary {
  glossary_terms: number;
  tm_segments: number;
  corpus_pairs: number;
  benchmark_items: number;
}

@Component({
  selector: 'app-language-assets-page',
  standalone: true,
  imports: [CommonModule, FormsModule, SscShellComponent],
  templateUrl: './language-assets.html',
  styleUrl: './language-assets.scss',
})
export class LanguageAssetsPage implements OnInit {
  protected activeTab: AssetTab = 'glossary';
  protected searchText = '';
  protected summary: Summary = { glossary_terms: 0, tm_segments: 0, corpus_pairs: 0, benchmark_items: 0 };
  protected glossaryRows: Record<string, unknown>[] = [];
  protected tmRows: Record<string, unknown>[] = [];
  protected corpusRows: Record<string, unknown>[] = [];
  protected benchmarkRows: Record<string, unknown>[] = [];
  protected saving = false;
  protected errorMessage = '';

  protected glossaryForm = {
    source_term: '',
    target_term: '',
    source_language: '',
    target_language: '',
    subject: '',
    definition: '',
    context_note: '',
    status: 'Draft',
  };

  protected tmForm = {
    source_text: '',
    target_text: '',
    source_language: '',
    target_language: '',
    subject: '',
    status: 'Approved',
  };

  protected corpusForm = {
    source_text: '',
    target_text: '',
    source_language: '',
    target_language: '',
    subject: '',
    corpus_type: 'Historical',
    source_file: '',
    status: 'Active',
  };

  protected benchmarkForm = {
    source_text: '',
    reference_translation: '',
    source_language: '',
    target_language: '',
    subject: '',
    dataset_name: 'Default',
    difficulty: '',
    status: 'Active',
  };

  constructor(
    private readonly httpSvc: Httpservice,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadSummary();
    this.loadAllTabs();
  }

  protected switchTab(tab: AssetTab): void {
    this.activeTab = tab;
    this.searchText = '';
    this.loadActiveTab();
  }

  protected search(): void {
    this.loadActiveTab();
  }

  protected saveGlossary(): void {
    if (!this.glossaryForm.source_term.trim() || !this.glossaryForm.target_term.trim()) {
      this.errorMessage = 'Source and target terms are required.';
      return;
    }
    this.saveAsset(AuthEndPoints.LANGUAGE_ASSETS_GLOSSARY, this.glossaryForm, () => {
      this.glossaryForm.source_term = '';
      this.glossaryForm.target_term = '';
      this.glossaryForm.definition = '';
      this.glossaryForm.context_note = '';
    });
  }

  protected saveTm(): void {
    if (!this.tmForm.source_text.trim() || !this.tmForm.target_text.trim()) {
      this.errorMessage = 'Source and target text are required.';
      return;
    }
    this.saveAsset(AuthEndPoints.LANGUAGE_ASSETS_TM, this.tmForm, () => {
      this.tmForm.source_text = '';
      this.tmForm.target_text = '';
    });
  }

  protected saveCorpus(): void {
    if (!this.corpusForm.source_text.trim()) {
      this.errorMessage = 'Source text is required.';
      return;
    }
    this.saveAsset(AuthEndPoints.LANGUAGE_ASSETS_CORPUS, this.corpusForm, () => {
      this.corpusForm.source_text = '';
      this.corpusForm.target_text = '';
      this.corpusForm.source_file = '';
    });
  }

  protected saveBenchmark(): void {
    if (!this.benchmarkForm.source_text.trim() || !this.benchmarkForm.reference_translation.trim()) {
      this.errorMessage = 'Source text and reference translation are required.';
      return;
    }
    this.saveAsset(AuthEndPoints.LANGUAGE_ASSETS_BENCHMARK, this.benchmarkForm, () => {
      this.benchmarkForm.source_text = '';
      this.benchmarkForm.reference_translation = '';
    });
  }

  protected text(value: unknown): string {
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  private saveAsset(endpoint: string, payload: unknown, reset: () => void): void {
    this.saving = true;
    this.errorMessage = '';
    this.httpSvc.requestCall(endpoint, ApiMethod.POST, payload, false, { suppressToast: true }).subscribe({
      next: (response) => {
        if (response?.status === 'FALSE') {
          this.errorMessage = response?.msg || 'Unable to save language asset.';
        } else {
          this.prependSavedAsset(response);
          reset();
          this.loadSummary();
          this.loadAllTabs();
        }
        this.saving = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Unable to save language asset.';
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }

  private loadSummary(): void {
    this.httpSvc.requestCall(AuthEndPoints.LANGUAGE_ASSETS_SUMMARY, ApiMethod.GET, '', false, { suppressToast: true }).subscribe({
      next: (response) => {
        const value = this.isRecord(response?.value) ? response.value : {};
        this.summary = {
          glossary_terms: Number(value['glossary_terms'] ?? 0),
          tm_segments: Number(value['tm_segments'] ?? 0),
          corpus_pairs: Number(value['corpus_pairs'] ?? 0),
          benchmark_items: Number(value['benchmark_items'] ?? 0),
        };
        this.cdr.detectChanges();
      },
    });
  }

  private loadActiveTab(): void {
    this.loadTab(this.activeTab);
  }

  private loadAllTabs(): void {
    (['glossary', 'tm', 'corpus', 'benchmark'] as AssetTab[]).forEach((tab) => this.loadTab(tab));
  }

  private loadTab(tab: AssetTab): void {
    const endpoints: Record<AssetTab, string> = {
      glossary: AuthEndPoints.LANGUAGE_ASSETS_GLOSSARY,
      tm: AuthEndPoints.LANGUAGE_ASSETS_TM,
      corpus: AuthEndPoints.LANGUAGE_ASSETS_CORPUS,
      benchmark: AuthEndPoints.LANGUAGE_ASSETS_BENCHMARK,
    };
    const search = tab === this.activeTab ? this.searchText : '';
    const query = `?search=${encodeURIComponent(search)}&limit=50`;
    this.httpSvc.requestCall(endpoints[tab], ApiMethod.GET, query, false, { suppressToast: true }).subscribe({
      next: (response) => {
        const items = this.extractItems(response);
        this.assignRows(tab, items);
        this.cdr.detectChanges();
      },
    });
  }

  private prependSavedAsset(response: unknown): void {
    if (!this.isRecord(response) || !this.isRecord(response['value'])) {
      return;
    }
    const savedRow = response['value'];
    const rowId = savedRow['id'];
    const prepend = (rows: Record<string, unknown>[]) => [
      savedRow,
      ...rows.filter((row) => row['id'] !== rowId),
    ];

    if (this.activeTab === 'glossary') this.glossaryRows = prepend(this.glossaryRows);
    if (this.activeTab === 'tm') this.tmRows = prepend(this.tmRows);
    if (this.activeTab === 'corpus') this.corpusRows = prepend(this.corpusRows);
    if (this.activeTab === 'benchmark') this.benchmarkRows = prepend(this.benchmarkRows);
  }

  private assignRows(tab: AssetTab, items: Record<string, unknown>[]): void {
    if (tab === 'glossary') this.glossaryRows = items;
    if (tab === 'tm') this.tmRows = items;
    if (tab === 'corpus') this.corpusRows = items;
    if (tab === 'benchmark') this.benchmarkRows = items;
  }

  private extractItems(response: unknown): Record<string, unknown>[] {
    if (!this.isRecord(response)) {
      return [];
    }
    const value = response['value'];
    if (this.isRecord(value) && Array.isArray(value['items'])) {
      return value['items'].filter(this.isRecord);
    }
    return [];
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
